import React from 'react';
import { useAuthSessionStore } from '@/presentation/auth/useAuthSessionStore';
import { WorkspaceApiClient } from '@/infrastructure/workspace/WorkspaceApiClient';
import { useAppTranslation } from '@/i18n/useAppTranslation';

export default function AccountManagementPage({ session }) {
  const { t } = useAppTranslation();
  const updateSessionUser = useAuthSessionStore((s) => s.updateSessionUser);
  const api = React.useMemo(() => new WorkspaceApiClient(), []);
  const token = session?.accessToken || null;
  const [name, setName] = React.useState(session?.user?.name || '');
  const [email, setEmail] = React.useState(session?.user?.email || '');
  const [password, setPassword] = React.useState('');
  const [result, setResult] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;
    if (!token) return undefined;
    api
      .getAccount(token)
      .then((data) => {
        if (!mounted) return;
        setName(data?.name || '');
        setEmail(data?.email || '');
      })
      .catch((e) => setResult(e.message || t('errors.loadAccount')));
    return () => {
      mounted = false;
    };
  }, [api, token, t]);

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <h1 className="font-headline text-3xl font-extrabold">{t('account.pageTitle')}</h1>
      <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
          <span className="font-semibold">{t('account.accountId')}</span> {session?.user?.id || 'N/A'}
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <label>
            <span className="mb-1 block text-sm font-semibold">{t('account.name')}</span>
            <input value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700 dark:bg-slate-800" />
          </label>
          <label>
            <span className="mb-1 block text-sm font-semibold">{t('account.email')}</span>
            <input value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700 dark:bg-slate-800" />
          </label>
          <label className="md:col-span-2">
            <span className="mb-1 block text-sm font-semibold">{t('account.changePassword')}</span>
            <input
              value={password}
              type="password"
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('account.newPasswordPlaceholder')}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700 dark:bg-slate-800"
            />
          </label>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={async () => {
              if (!token) return;
              setLoading(true);
              try {
                await api.updateAccount({ name, email, password }, token);
                updateSessionUser({ name, email });
                setResult(t('account.saved'));
                setPassword('');
              } catch (e) {
                setResult(e.message || t('account.saveFailed'));
              } finally {
                setLoading(false);
              }
            }}
            className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-bold text-white"
          >
            {loading ? t('account.saving') : t('account.save')}
          </button>
          <button
            onClick={async () => {
              if (!token) return;
              try {
                const ticket = await api.createSupportTicket({
                category: 'reset_password',
                subject: 'Password reset request',
                message: `User ${email || session?.user?.id || 'unknown'} yêu cầu reset mật khẩu.`,
                priority: 'high',
              }, token);
                setResult(t('account.resetTicketSent', { id: ticket.id }));
              } catch (e) {
                setResult(e.message || t('account.createTicketFailed'));
              }
            }}
            className="rounded-lg border border-amber-300 px-4 py-2 text-sm font-bold text-amber-700"
          >
            {t('account.resetTicket')}
          </button>
        </div>
        {result ? <p className="mt-3 text-sm font-semibold text-emerald-700">{result}</p> : null}
      </div>
    </div>
  );
}

